import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback
} from 'react';
import {
  string, bool, shape, number, func
} from 'prop-types';
import {
  ExperienceContext,
  useStore,
  useConfigService
} from '@thd-nucleus/experience-context';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { Placeholder } from '@thd-olt-component-react/core-ui';
import { Carousel } from '@one-thd/sui-carousel';
import { Card, CardTitle, Typography } from '@one-thd/sui-atomic-components';
import { ProductPodUtils } from '@thd-olt-component-react/product-pod';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import {
  useDataModel,
  alias,
  customType,
  extend,
  params,
  string as dString,
  shape as dShape,
  hoist,
  arrayOf,
  QueryProvider,
} from '@thd-nucleus/data-sources';

import { SponsoredProductPod } from '../SponsoredProductPod';
import { makeApiRequest, shouldMakeApiRequest } from '../../utils/requestHelpers';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import {
  getSchema,
  getSkus,
  handleLoadingState,
  isExperienceDataResolved,
  mapSearchModel,
  showCarouselPlaceholder,
  triggerNewRelic
} from '../../utils/helpers';
import { carouselTypes, PRODUCT } from '../../constants/constants';
import './SponsoredCarousel.scss';

const propTypes = {
  browserId: string,
  customRenderId: string,
  slugId: string,
  isSinglePLA: bool,
  pageContext: shape({
    label: string,
    data: shape(),
    keyword: string,
    isSearch: bool,
    isCategory: bool,
    isBrowse: bool,
    schema: string,
    slugId: string
  }),
  slidesPer: number,
  noATCFulfillment: bool,
  utilizeRvData: bool,
  mockEnabledFeatureSwitch: bool,
  showSponsoredCarousel: bool,
  hideCarouselArrows: bool,
  dataSource: string,
  enableForChapters: bool
};

const defaultProps = {
  browserId: '',
  customRenderId: '',
  slugId: '',
  pageContext: {},
  slidesPer: null,
  noATCFulfillment: null,
  utilizeRvData: false,
  mockEnabledFeatureSwitch: false,
  showSponsoredCarousel: false,
  isSinglePLA: false,
  hideCarouselArrows: false,
  dataSource: 'searchNav',
  enableForChapters: false
};

const SponsoredCarouselComponent = (props) => {
  const {
    browserId,
    customRenderId,
    slugId,
    pageContext,
    isSinglePLA,
    slidesPer,
    noATCFulfillment,
    mockEnabledFeatureSwitch,
    showSponsoredCarousel,
    hideCarouselArrows,
    dataSource,
    enableForChapters
  } = props;

  const experienceContext = useContext(ExperienceContext);
  const validBrowserId = browserId || experienceContext?.cookie?.adobeCookie?.MCMID;

  const renderNewCarouselPip = useConfigService('fs-prop:pip-master__sponsored-carousel-nucleus--renderNewCarousel') || enableForChapters;
  const renderNewCarouselBrowseAndSearch = useConfigService('fs-prop:browse-and-search-master__sponsored-carousel-nucleus--renderNewCarousel') || enableForChapters;
  let renderNewCarousel = renderNewCarouselPip || renderNewCarouselBrowseAndSearch;

  const { storeId, isLocalized, storeZip } = useStore({
    varnish: true,
    online: true
  });
  const { utilizeRvData } = props;
  let breakpoints = {};
  if (!slidesPer) {
    breakpoints = Carousel.defaultProps.breakpoints;
  } else {
    breakpoints = {
      sm: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      md: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      lg: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      xl: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
    };
  }

  const { schema, schemaId } = useMemo(() => {
    const schemaName = getSchema({ schema: pageContext?.schema, pageContext, props });
    return { schema: schemaName, schemaId: schemaName + '__carousel' };
  }, [props]);

  const [lastRequestScenario, setLastRequestScenario] = useState({});
  const [pageId, setPageId] = useState('');
  const [products, setProducts] = useState([]);
  const [atcFilteredProducts, setATCFilteredProducts] = useState([]);
  const [carouselData, setCarouselData] = useState({});
  const [serverError, setServerError] = useState(false);
  const [loadingState, setLoadingState] = useState(true);
  const [isAPICallCompleted, setIsAPICallCompleted] = useState(false);

  const setAdProducts = useCallback((adProducts) => {
    if (adProducts && adProducts.length) {
      setProducts(adProducts
        .map((product) => ({
          ...product,
          onViewBeacon: product.onViewBeacon ? product.onViewBeacon.replace('https:', '') : '',
          ITC: `AUC-${product?.campaignId || ''}-${product?.placementId || ''}-${product?.slotId || ''}`
        }))
      );
    }
  }, []);

  if (showSponsoredCarousel
    || mockEnabledFeatureSwitch
    || schema === 'hp_sponsored_auth'
    || schema === 'hp_sponsored'
    || schema === 'cat_sponsored'
    || schema === 'cart_sponsored'
    || schema === 'thankyoupage_sponsored') {
    renderNewCarousel = true;
  }

  useEffect(() => {
    const isResolvedContext = isExperienceDataResolved({ isLocalized, experienceContext });
    const { label: pageLabel, isPip, isStorePage } = pageContext || {};
    const isStandardCarousel = (
      pageLabel === 'browse-search'
      || isPip
      || isStorePage
      || schema === 'hp_sponsored'
      || schema === 'hp_sponsored_auth'
      || schema === 'cat_sponsored'
      || schema === 'cart_sponsored'
      || schema === 'thankyoupage_sponsored'
    );

    if (renderNewCarousel && isResolvedContext && isStandardCarousel) {

      const isValidScenario = shouldMakeApiRequest({
        lastRequestScenario,
        experienceContext,
        pageContext,
        customRenderId,
        browserId: validBrowserId,
        schema
      });

      if ((isStandardCarousel && isValidScenario) || enableForChapters) {
        setLastRequestScenario({
          url: window.location.pathname + window.location.search,
          skus: getSkus(pageContext)
        });

        makeApiRequest({
          browserId: validBrowserId,
          schema,
          schemaId,
          experienceContext,
          pageContext,
          carouselType: pageContext?.isPip && isSinglePLA ? carouselTypes.PLA : null,
          show: PRODUCT,
          setCarouselData,
          setIsAPICallCompleted,
          setPageId,
          setAdProducts,
          utilizeRvData,
          slugId,
          setServerError,
        });
      }
    }

  }, [pageContext?.data, isLocalized, experienceContext.mounted, slugId, validBrowserId]);

  useEffect(() => {
    const isValidScenario = shouldMakeApiRequest({
      lastRequestScenario,
      experienceContext,
      pageContext,
      customRenderId,
      browserId: validBrowserId,
      schema
    });
    if (showSponsoredCarousel
   && experienceContext.mounted
    && isLocalized && validBrowserId
    && !isValidScenario) {

      makeApiRequest({
        browserId: validBrowserId,
        schema,
        schemaId,
        experienceContext,
        pageContext,
        carouselType: pageContext?.isPip && isSinglePLA ? carouselTypes.PLA : null,
        show: PRODUCT,
        setCarouselData,
        setIsAPICallCompleted,
        setPageId,
        setAdProducts,
        utilizeRvData,
        slugId,
        setServerError,
      });
    }
  }, [showSponsoredCarousel, experienceContext.mounted, isLocalized, validBrowserId]);

  const skip = !products?.length;
  const resp = useDataModel('sponsoredCarousel', {
    ssr: false,
    skip,
    variables: {
      itemIds: products?.map((item) => item.id.toString()) || [],
      storeId,
      additionalSearchParams: {
        deliveryZip: experienceContext?.deliveryZip || storeZip
      }
    },
  });

  const { data, loading, error } = resp;

  useEffect(() => {
    setLoadingState(handleLoadingState({
      products, atcFilteredProducts, error, serverError, isAPICallCompleted
    }));
  }, [products, atcFilteredProducts, error, serverError, isAPICallCompleted]);
  useEffect(() => {
    initImpressionWatchers(['NUCLEUS_CAROUSEL']);
  }, [data]);

  useEffect(() => {
    const filteredProducts = [];
    let productsRemovedCounter = 0;
    const { targetingSource, adsFiltered = 0 } = carouselData;
    if (products && products.length && data?.searchModel?.products?.length) {
      products.forEach((product) => {
        const productData = data.searchModel.products.find(({ itemId }) => itemId === `${product.id}`);

        if (productData) {
          const isATCEnabled = ProductPodUtils.isATCEnabled(productData);

          if (isATCEnabled) {
            const updatedProduct = mapSearchModel(product, productData);
            filteredProducts.push(updatedProduct);
          } else {
            productsRemovedCounter += 1;

          }
        }
      });
      setATCFilteredProducts(filteredProducts);
    }

    if (productsRemovedCounter > 0) {
      triggerNewRelic(`sponsoredBottomCarousel-adsFiltered-${targetingSource}`,
        adsFiltered + productsRemovedCounter);
    }

  }, [products, data, error, serverError, carouselData?.targetingSource, loadingState]);

  if (showCarouselPlaceholder(renderNewCarousel, experienceContext, pageContext, loadingState)) {
    let slides = [];
    let textPlaceholder = [...Array(16)].map((value, idx) => <Placeholder type="text" key={idx.toString()} />);
    for (let i = 0; i < 16; i += 1) {
      slides.push(
        <div key={i.toString()} style={{ width: '100%' }}>
          <Placeholder
            type="rect"
            height="150px"
            width="100%"
          />
          {textPlaceholder}
        </div>
      );
    }
    return (
      <div className="sui-w-full" data-component="SponsoredCarouselPlaceholder">
        <Carousel breakpoints={breakpoints} hideCarouselArrows={hideCarouselArrows} disableMargin>
          {slides}
        </Carousel>
      </div>
    );
  }

  return (
    <>
      {(!serverError && atcFilteredProducts.length && renderNewCarousel) ? (
        <div className="sui-w-full" id="sponsored-carousel-nucleus" data-component="SponsoredCarousel">
          <div
            id={schema}
            className="sponsored-nucleus-carousel"
            data-testid="sponsored-pla-carousel"
          >
            <meta data-prop="name" content={schema} />
            <Card className="sui-h-full sui-leading-none">
              <div className="sponsored-nucleus-carousel__title">
                <CardTitle
                  header={(
                    <Typography variant="h3" weight="regular">
                      Sponsored Products
                    </Typography>
                  )}
                />
              </div>
              <QueryProvider
                cacheKey="sponsored-product-pod"
                dataSource={dataSource}
                defaultVariables={{ storeId }}
              >
                <Carousel breakpoints={breakpoints} hideCarouselArrows={hideCarouselArrows} disableMargin>
                  {atcFilteredProducts.map((product, idx) => {
                    return (
                      <SponsoredProductPod
                        key={product.id}
                        product={product}
                        pageId={pageId}
                        storeId={storeId}
                        position={idx + 1}
                        schema={schema}
                        noATCFulfillment={noATCFulfillment}
                        targetingSource={carouselData?.targetingSource}
                      />
                    );
                  })}
                </Carousel>
              </QueryProvider>
            </Card>
          </div>
        </div>
      ) : null}
    </>
  );

};

SponsoredCarouselComponent.propTypes = propTypes;
SponsoredCarouselComponent.defaultProps = defaultProps;

// check for deliveryZip
// if not avilable send storeZip
SponsoredCarouselComponent.dataModel = {
  sponsoredCarousel: alias('searchModel').params({
    itemIds: arrayOf(dString()),
    storeId: dString(),
    additionalSearchParams: customType('AdditionalParams').shape({
      deliveryZip: dString()
    })
  }).shape(({
    products: arrayOf(dShape(SponsoredProductPod.dataModel.product))
  }))
};

/* eslint react/jsx-props-no-spreading: 0 */
const QueryProvidedSponsoredCarousel = (props) => (
  <QueryProvider cacheKey="sponsored-nucleus-carousel">
    <SponsoredCarouselComponent {...props} />
  </QueryProvider>
);

QueryProvidedSponsoredCarousel.propTypes = propTypes;

QueryProvidedSponsoredCarousel.defaultProps = defaultProps;

QueryProvidedSponsoredCarousel.dataModel = extend({}, SponsoredCarouselComponent);

const SponsoredCarousel = withErrorBoundary(
  withDynamicComponent(
    withHydrator(
      {
        id: 'sponsored-carousel-nucleus',
        preserveCtxVal: 'clientStore'
      },
      QueryProvidedSponsoredCarousel
    )
  )
);
SponsoredCarousel.displayName = 'SponsoredCarousel';
export { SponsoredCarousel };
